<template>
  <div v-if="showModal" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark">
          <h5 class="modal-title">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ $t('lasa.alert.title') }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal" :disabled="loading"></button>
        </div>
        
        <div class="modal-body">
          <!-- Alert Message -->
          <div class="alert alert-warning mb-4" role="alert">
            <strong>{{ $t('lasa.alert.warning') }}</strong> {{ alertMessage }}
          </div>
          
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-warning" role="status">
              <span class="visually-hidden">{{ $t('common.loading') }}</span>
            </div>
            <p class="mt-3 text-muted">{{ $t('lasa.alert.checking') }}</p>
          </div>
          
          <!-- LASA Groups Display -->
          <div v-else-if="lasaGroups.length > 0">
            <div v-for="(group, index) in lasaGroups" :key="group.id" class="lasa-group mb-4">
              <div class="card border-warning">
                <div class="card-header bg-light">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">
                      <span class="badge bg-warning text-dark me-2">{{ $t('lasa.alert.group') }} {{ index + 1 }}</span>
                      {{ group.name }}
                    </h6>
                    <span :class="getRiskBadgeClass(group.risk_level)">
                      {{ getRiskLevelText(group.risk_level) }}
                    </span>
                  </div>
                  <small class="text-muted">{{ group.description }}</small>
                </div>
                
                <div class="card-body">
                  <p class="text-danger fw-bold mb-3">
                    <i class="fas fa-info-circle me-1"></i>
                    {{ $t('lasa.alert.similar_products') }}
                  </p>
                  
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead class="table-light">
                        <tr>
                          <th style="width: 40%">{{ $t('product.name') }}</th>
                          <th style="width: 20%">{{ $t('product.barcode') }}</th>
                          <th style="width: 20%">{{ $t('lasa.type') }}</th>
                          <th style="width: 20%" class="text-center">{{ $t('cart.status') }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="product in group.products" :key="product.id">
                          <td>
                            <strong>{{ product.name }}</strong>
                            <br>
                            <small class="text-muted">{{ product.generic_name || '-' }}</small>
                          </td>
                          <td>
                            <code>{{ product.barcode }}</code>
                          </td>
                          <td>
                            <span :class="getTypeBadgeClass(product.lasa_type)">
                              {{ getTypeText(product.lasa_type) }}
                            </span>
                          </td>
                          <td class="text-center">
                            <span v-if="isInCart(product.id)" class="badge bg-danger">
                              <i class="fas fa-shopping-cart me-1"></i>
                              {{ $t('cart.in_cart') }}
                            </span>
                            <span v-else class="badge bg-secondary">
                              {{ $t('cart.not_in_cart') }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <!-- Recommendations -->
                  <div v-if="group.recommendations" class="alert alert-info mt-3 mb-0">
                    <strong><i class="fas fa-lightbulb me-1"></i> {{ $t('lasa.recommendations') }}:</strong>
                    <p class="mb-0 mt-2">{{ group.recommendations }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- No Conflicts State -->
          <div v-else class="text-center py-4">
            <i class="fas fa-check-circle text-success fa-3x mb-3"></i>
            <p class="text-muted">{{ $t('lasa.alert.no_conflicts') }}</p>
          </div>
        </div>
        
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn btn-secondary" 
            @click="closeModal"
            :disabled="loading"
          >
            <i class="fas fa-times me-1"></i>
            {{ $t('common.close') }}
          </button>
          
          <button 
            v-if="lasaGroups.length > 0" 
            type="button" 
            class="btn btn-warning" 
            @click="acknowledgeAndContinue"
            :disabled="loading || acknowledging"
          >
            <i class="fas fa-check me-1" v-if="!acknowledging"></i>
            <span class="spinner-border spinner-border-sm me-1" v-else></span>
            {{ acknowledging ? $t('common.processing') : $t('lasa.alert.acknowledge') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LasaAlertModal',
  
  props: {
    cartItems: {
      type: Array,
      default: () => []
    },
    autoCheck: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      showModal: false,
      loading: false,
      acknowledging: false,
      lasaGroups: [],
      alertMessage: '',
      lastCheckedCart: null
    }
  },
  
  watch: {
    cartItems: {
      handler(newCart) {
        if (this.autoCheck && this.hasCartChanged(newCart)) {
          this.checkLasaConflicts();
        }
      },
      deep: true
    }
  },
  
  methods: {
    /**
     * Check for LASA conflicts in the current cart
     */
    async checkLasaConflicts() {
      if (this.cartItems.length === 0) {
        return;
      }
      
      this.loading = true;
      
      try {
        const productIds = this.cartItems.map(item => item.product_id || item.id);
        
        const response = await axios.post('/pos-cart/check-lasa', {
          product_ids: productIds
        });
        
        if (response.data.success && response.data.has_conflicts) {
          this.lasaGroups = response.data.lasa_groups || [];
          this.alertMessage = response.data.message || this.$t('lasa.alert.default_message');
          this.showModal = true;
          
          // Store current cart state
          this.lastCheckedCart = JSON.parse(JSON.stringify(this.cartItems));
          
          // Emit event for parent component
          this.$emit('lasa-conflict-detected', {
            groups: this.lasaGroups,
            severity: response.data.max_risk_level
          });
        }
      } catch (error) {
        console.error('LASA check error:', error);
        
        // Only show error if it's not a validation error (which means no conflicts)
        if (error.response?.status !== 422) {
          this.$emit('lasa-check-error', error);
        }
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Check if cart contents have changed
     */
    hasCartChanged(newCart) {
      if (!this.lastCheckedCart) {
        return true;
      }
      
      const newIds = newCart.map(item => item.product_id || item.id).sort();
      const oldIds = this.lastCheckedCart.map(item => item.product_id || item.id).sort();
      
      return JSON.stringify(newIds) !== JSON.stringify(oldIds);
    },
    
    /**
     * Check if a product is in the cart
     */
    isInCart(productId) {
      return this.cartItems.some(item => 
        (item.product_id || item.id) === productId
      );
    },
    
    /**
     * Get CSS class for risk level badge
     */
    getRiskBadgeClass(riskLevel) {
      const classes = {
        'high': 'badge bg-danger',
        'medium': 'badge bg-warning text-dark',
        'low': 'badge bg-info'
      };
      return classes[riskLevel] || 'badge bg-secondary';
    },
    
    /**
     * Get human-readable risk level text
     */
    getRiskLevelText(riskLevel) {
      const texts = {
        'high': this.$t('lasa.risk.high'),
        'medium': this.$t('lasa.risk.medium'),
        'low': this.$t('lasa.risk.low')
      };
      return texts[riskLevel] || riskLevel;
    },
    
    /**
     * Get CSS class for LASA type badge
     */
    getTypeBadgeClass(lasaType) {
      const classes = {
        'look_alike': 'badge bg-primary',
        'sound_alike': 'badge bg-success',
        'both': 'badge bg-danger'
      };
      return classes[lasaType] || 'badge bg-secondary';
    },
    
    /**
     * Get human-readable LASA type text
     */
    getTypeText(lasaType) {
      const texts = {
        'look_alike': this.$t('lasa.type.look_alike'),
        'sound_alike': this.$t('lasa.type.sound_alike'),
        'both': this.$t('lasa.type.both')
      };
      return texts[lasaType] || lasaType;
    },
    
    /**
     * Acknowledge alert and continue with sale
     */
    async acknowledgeAndContinue() {
      this.acknowledging = true;
      
      try {
        // Get all alert IDs from the response
        const alertIds = this.lasaGroups
          .flatMap(group => group.alert_ids || [])
          .filter(id => id); // Remove null/undefined
        
        if (alertIds.length > 0) {
          // Acknowledge all alerts
          await Promise.all(
            alertIds.map(alertId => 
              axios.post(`/pharmacy-safety/lasa-alerts/${alertId}/acknowledge`, {
                acknowledged_by: window.currentUserId || null,
                notes: 'Acknowledged from POS'
              })
            )
          );
        }
        
        this.$emit('lasa-acknowledged', {
          groups: this.lasaGroups,
          alert_ids: alertIds
        });
        
        this.closeModal();
      } catch (error) {
        console.error('Error acknowledging LASA alerts:', error);
        this.$emit('lasa-acknowledge-error', error);
      } finally {
        this.acknowledging = false;
      }
    },
    
    /**
     * Close the modal
     */
    closeModal() {
      this.showModal = false;
      this.lasaGroups = [];
      this.alertMessage = '';
      this.$emit('modal-closed');
    },
    
    /**
     * Manually trigger LASA check (for external use)
     */
    triggerCheck() {
      this.checkLasaConflicts();
    },
    
    /**
     * Reset component state
     */
    reset() {
      this.showModal = false;
      this.loading = false;
      this.acknowledging = false;
      this.lasaGroups = [];
      this.alertMessage = '';
      this.lastCheckedCart = null;
    }
  },
  
  mounted() {
    // Initial check if cart has items
    if (this.autoCheck && this.cartItems.length > 0) {
      this.checkLasaConflicts();
    }
  }
}
</script>

<style scoped>
.modal {
  z-index: 1050;
}

.lasa-group {
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}

.card-header {
  border-bottom: 2px solid #ffc107;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}

code {
  color: #d63384;
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
}

.btn-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Scrollbar styling */
.table-responsive::-webkit-scrollbar {
  width: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #ffc107;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #ffb300;
}
</style>
